import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { AccountCompanyService } from '../services/account-company.service';
import { AccountingCompanyContextService } from '../services/accounting-company-context.service';
import { CurrentCompanyService } from '../services/current-company.service';

/**
 * Requires an active company in session before entering the main application shell.
 * When no company is open, auto-selects when only one company is available; otherwise
 * routes to /accounting and opens the login company selection modal.
 */
export const companySelectedGuard: CanActivateFn = (_route, state) => {
  const platformId = inject(PLATFORM_ID);
  const auth = inject(AuthService);
  const companyService = inject(AccountCompanyService);
  const companyContext = inject(AccountingCompanyContextService);
  const currentCompanyService = inject(CurrentCompanyService);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  if (!auth.isLoggedIn()) {
    return true;
  }

  if (companyService.isCompanyOpen()) {
    return true;
  }

  if (companyContext.loginCompanySelectionPending()) {
    return true;
  }

  const parsed = router.parseUrl(state.url);
  const queryReturnUrl = parsed.queryParams['returnUrl'];
  const returnUrl =
    typeof queryReturnUrl === 'string' && queryReturnUrl.startsWith('/')
      ? queryReturnUrl
      : state.url && state.url !== '/' && !state.url.startsWith('/accounting')
        ? state.url
        : undefined;

  return currentCompanyService.resolveLoginCompanySelection(returnUrl).pipe(
    switchMap((result) => {
      if (result === 'failed') {
        return of(router.createUrlTree(['/login']));
      }

      if (!state.url.startsWith('/accounting')) {
        return of(
          router.createUrlTree(
            ['/accounting'],
            returnUrl ? { queryParams: { returnUrl } } : {},
          ),
        );
      }

      return of(true);
    }),
  );
};
